// UserContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Firebase
import { getIdTokenResult, onIdTokenChanged } from 'firebase/auth';
import { auth } from 'firebaseConfig';

// Security
import { hasSuperUserClaim } from 'security/superUser';

// Utilities
import { ProfileTab } from 'utilities/Users';

// Context
export const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export const UserProvider = ({ children }) => {

  /**
   * The user object contains current user and user preference information.
   */
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authClaims, setAuthClaims] = useState({});
  const [isAuthClaimsLoading, setIsAuthClaimsLoading] = useState(true);

  /**
   * User currently being viewed by the current user.
   */
  const [displayedUser, setDisplayedUser] = useState(null);

  /**
   * This user-specific state variables are preloaded when the 
   * user is loaded, tied to the user, and updated using real-time updates.
   */
  const [userEngagements, setUserEngagements] = useState([]);
  const [userNotifications, setUserNotifications] = useState([]);
  const [isUserSettingsLoading, setIsUserSettingsLoading] = useState(true);
  const [userSettings, setUserSettings] = useState(null);
  const [userStorageUsage, setUserStorageUsage] = useState(0); // Bytes
  const [userStreamingUsage, setUserStreamingUsage] = useState(0); // Seconds
  const [activeProfileTab, setActiveProfileTab] = useState(ProfileTab.NODES);

  /**
   * Quotas
   */
  const [userStorageQuotaReached, setUserStorageQuotaReached] = useState(true);
  const [userStreamingQuotaReached, setUserStreamingQuotaReached] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onIdTokenChanged(auth, async (authUser) => {
      if (!isMounted) return;

      if (!authUser) {
        setAuthClaims({});
        setIsAuthClaimsLoading(false);
        return;
      }

      setIsAuthClaimsLoading(true);

      try {
        const tokenResult = await getIdTokenResult(authUser);
        if (!isMounted) return;

        setAuthClaims(tokenResult?.claims && typeof tokenResult.claims === 'object'
          ? tokenResult.claims
          : {});
      } catch (_error) {
        if (!isMounted) return;
        setAuthClaims({});
      } finally {
        if (isMounted) {
          setIsAuthClaimsLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const isSuperUser = hasSuperUserClaim(authClaims);

  const value = useMemo(() => ({

    // User
    user, setUser,
    isAuthLoading, setIsAuthLoading,
    authClaims,
    isAuthClaimsLoading,
    isSuperUser,

    // User Being Viewed
    displayedUser, setDisplayedUser,

    // User Preloads
    userEngagements, setUserEngagements,
    userNotifications, setUserNotifications,
    isUserSettingsLoading, setIsUserSettingsLoading,
    userSettings, setUserSettings,
    userStorageUsage, setUserStorageUsage,
    userStreamingUsage, setUserStreamingUsage,

    // Quotas
    userStorageQuotaReached, setUserStorageQuotaReached,
    userStreamingQuotaReached, setUserStreamingQuotaReached,

    // Profile Tab
    activeProfileTab, setActiveProfileTab,

  }), [
    activeProfileTab,
    authClaims,
    isAuthLoading,
    isAuthClaimsLoading,
    isSuperUser,
    displayedUser,
    isUserSettingsLoading,
    user,
    userEngagements,
    userNotifications,
    userSettings,
    userStorageQuotaReached,
    userStorageUsage,
    userStreamingQuotaReached,
    userStreamingUsage,
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
